import type { ReactNode } from 'react';
import { MDC_DEGREES } from '@erectus/shared';
import type { SessionSummary } from '@erectus/shared';
import { Card, SectionTitle } from '@/ui/primitives.js';
import { useAuth } from '@/stores/auth.js';
import { useWarm } from '@/lib/warmCache.js';
import { loaders, warmKeys } from '@/lib/warmup.js';
import { useHabits } from '@/features/program/useProgram.js';
import { formatMinute } from './Timeline.js';

type Session = SessionSummary & { id: string };

/**
 * This check against the one before it, and the habits logged in between —
 * breaks and exercise snacks. Side by side so a user (and later the team, in
 * aggregate) can see whether weeks with more breaks and snacks go with
 * steadier sitting (docs/PRODUCT.md §12). Change inside ±MDC is "about the
 * same": smaller than the measurement error.
 */
export function SinceLastCheck({ sessionId, summary }: { sessionId: string; summary: SessionSummary }): ReactNode {
  const { user } = useAuth();
  const [state] = useWarm<Session[]>(
    user ? warmKeys.sessions(user.uid) : null,
    () => loaders.sessions(user!.uid),
    'Could not load your history.',
  );
  const habits = useHabits();
  if (state.status !== 'ready') return null;

  const previous = state.data
    .filter((s) => s.id !== sessionId && s.startedAt < summary.startedAt)
    .sort((a, b) => b.startedAt - a.startedAt)[0];

  if (!previous) {
    return (
      <Card className="space-y-1">
        <SectionTitle>Since your last check</SectionTitle>
        <p className="text-sm text-muted">
          This is your first check. The next one will be compared with it, along with the breaks and exercise snacks
          you log in between.
        </p>
      </Card>
    );
  }

  const between = (at: number): boolean => at >= previous.endedAt && at < summary.startedAt;
  const breaks = habits.breaks.filter((b) => between(b.at)).length;
  const snacks = habits.snacks.filter((s) => between(s.at)).length;
  const days = Math.max(1, Math.round((summary.startedAt - previous.endedAt) / 86_400_000));

  // A v1 session has no `cva` (its head angle was on another scale): no comparison.
  const cvaNow = summary.cva?.mean ?? null;
  const cvaThen = previous.cva?.mean ?? null;
  const cvaDelta = cvaNow !== null && cvaThen !== null ? cvaNow - cvaThen : null;

  return (
    <Card className="space-y-3">
      <SectionTitle>Since your last check</SectionTitle>
      <p className="text-xs text-muted">
        Compared with {new Date(previous.startedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} ·{' '}
        {days} {days === 1 ? 'day' : 'days'} apart
      </p>
      <dl className="grid grid-cols-2 gap-3 text-sm">
        <Change label="Score" now={summary.score} then={previous.score} format={(v) => String(Math.round(v))} />
        <Change
          label="Good posture"
          now={summary.goodRatio * 100}
          then={previous.goodRatio * 100}
          format={(v) => `${Math.round(v)}%`}
        />
        <div>
          <dt className="text-muted">Neck angle</dt>
          <dd className="font-semibold">
            {cvaDelta === null
              ? '—'
              : Math.abs(cvaDelta) < MDC_DEGREES
                ? 'About the same'
                : `${cvaDelta > 0 ? '+' : ''}${cvaDelta.toFixed(0)}° ${cvaDelta > 0 ? 'more upright' : 'more forward'}`}
          </dd>
        </div>
        <div>
          <dt className="text-muted">First slip</dt>
          <dd className="font-semibold">
            {summary.timeToFirst === null ? 'Never' : formatMinute(summary.timeToFirst)}
            <span className="font-normal text-muted">
              {' '}
              (was {previous.timeToFirst === null ? 'never' : formatMinute(previous.timeToFirst)})
            </span>
          </dd>
        </div>
      </dl>
      <div className="rounded-xl bg-surface p-3 text-sm">
        <p className="font-semibold">In between</p>
        <p className="text-ink-soft">
          {breaks} rest {breaks === 1 ? 'break' : 'breaks'} and {snacks} exercise {snacks === 1 ? 'snack' : 'snacks'} logged
          {breaks + snacks > 0 ? ` — about ${(breaks / days).toFixed(1)} breaks and ${(snacks / days).toFixed(1)} snacks a day` : ''}.
        </p>
        {breaks + snacks === 0 ? (
          <p className="mt-1 text-xs text-muted">
            Log them on the Today card (Progress or Plan), so the next report can put them next to your posture.
          </p>
        ) : null}
      </div>
    </Card>
  );
}

function Change({
  label,
  now,
  then,
  format,
}: {
  label: string;
  now: number;
  then: number;
  format(v: number): string;
}): ReactNode {
  const delta = now - then;
  return (
    <div>
      <dt className="text-muted">{label}</dt>
      <dd className="font-semibold">
        {format(now)}{' '}
        <span className="font-normal text-muted">
          ({delta >= 0 ? '+' : ''}
          {Math.round(delta)} from {format(then)})
        </span>
      </dd>
    </div>
  );
}
